// src/main.jsx
import React from 'react';
import ReactDOM from 'react-dom/client';
import { ClerkProvider } from "@clerk/clerk-react";
import { BrowserRouter } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';

// 🔒 Privacy Enforcement
import { installPrivacyTripwire } from './utils/privacyTripwire';
installPrivacyTripwire();

import App from './App.jsx';
// Corrected import to use the actual AuthProvider
import { AuthProvider } from './context/AuthContext.jsx';
import AppErrorBoundary from './components/app/AppErrorBoundary.jsx';
import './index.css';

// 🛡️ AUTO-RECOVERY: Handle Vite chunk load errors after deploy
// Pattern: Failed to fetch dynamically imported module / Loading chunk X failed
const CHUNK_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Loading chunk .+ failed/i,
  /ChunkLoadError/i,
  /error loading dynamically imported module/i,
];

const isChunkError = (error) => {
  const message = error?.message || error?.reason?.message || String(error);
  return CHUNK_ERROR_PATTERNS.some(pattern => pattern.test(message));
};

const handleChunkError = () => {
  const RELOAD_KEY = 'vite_chunk_reload_attempted';
  
  // Only reload once to avoid loops
  if (!sessionStorage.getItem(RELOAD_KEY)) {
    console.warn('[main] Chunk load error detected, reloading page once...');
    sessionStorage.setItem(RELOAD_KEY, Date.now().toString());
    window.location.reload();
    return true; // Handled
  } else {
    console.error('[main] Chunk error persists after reload. Not reloading again.');
    return false; // Let error boundary show
  }
};

// Global error handlers
window.addEventListener('error', (event) => {
  if (isChunkError(event.error)) {
    event.preventDefault(); // Prevent default error UI
    handleChunkError();
  }
});

window.addEventListener('unhandledrejection', (event) => {
  if (isChunkError(event.reason)) {
    event.preventDefault();
    handleChunkError();
  }
});

// Clear reload flag on successful navigation (user moved on)
window.addEventListener('load', () => {
  // Clear after 5 seconds to allow fresh reloads if user navigates elsewhere
  setTimeout(() => {
    sessionStorage.removeItem('vite_chunk_reload_attempted');
  }, 5000);
});

ReactDOM.createRoot(document.getElementById('root')).render(
  <ClerkProvider publishableKey={import.meta.env.VITE_CLERK_PUBLISHABLE_KEY}>
    <React.StrictMode>
      <AppErrorBoundary>
        <HelmetProvider>
          <BrowserRouter>
            <AuthProvider>
              <App />
            </AuthProvider>
          </BrowserRouter>
        </HelmetProvider>
      </AppErrorBoundary>
    </React.StrictMode>
  </ClerkProvider>
);
